/**
 * Where a task link leads, and how the drawer names and marks it (DD-052).
 * Ideas and briefs open their page. Posts and events have none, so they
 * open the list that shows them.
 */

import type { ComponentType } from "react";
import type { IconProps } from "@/components/icons/types";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { PostStatus, VALID_STATUSES } from "@/core/types/postTypes";
import { homeTabRedirect } from "@/domains/Home/homeLinks";
import { PostStage, getStageByStatus } from "@/domains/PostCreator/utils/stageConfig";
import type { TaskLinkMeta, TaskLinkOption, TaskLinkRef, TaskLinkType, TaskViaItem } from "../types";

/** The picker's type tabs, in order. */
export const LINK_PICKER_TYPES: readonly TaskLinkType[] = ["idea", "brief", "post", "calendar_event"];

/** The picker searches this long after the last keystroke. */
export const LINK_SEARCH_DEBOUNCE_MS = 250;

/** What a path needs: a direct link, a carried link or a via item. */
export interface TaskLinkTarget {
  type: TaskLinkType;
  targetId: string;
  meta?: TaskLinkMeta;
}

function isPostStatus(value: string): value is PostStatus {
  return (VALID_STATUSES as readonly string[]).includes(value);
}

/** The posts list tab for a status. A status this bundle does not know,
 * or none, opens Tutti. */
export function postStageFor(status: string | undefined): PostStage {
  return status && isPostStatus(status) ? getStageByStatus(status) : "all";
}

/** The best page for a linked item (DD-052), as an absolute path (DD-003). */
export function taskLinkPath(link: TaskLinkTarget, clientId: string): string {
  const base = `/app/${clientId}`;
  switch (link.type) {
    case "idea":
      return `${base}/ideas/${link.targetId}`;
    case "brief":
      return `${base}/briefs/${link.targetId}`;
    case "post":
      if (link.meta?.postType === "story") return `${base}/stories`;
      return `${base}/posts?stage=${postStageFor(link.meta?.status)}`;
    case "calendar_event":
      return homeTabRedirect("calendar", clientId);
  }
}

/** A via group header's target. */
export function viaItemPath(item: TaskViaItem, clientId: string): string {
  return taskLinkPath({ type: item.type, targetId: item.id }, clientId);
}

export type LinkEntityKey = "idea" | "brief" | "post" | "story" | "calendarEvent";

export function linkEntityKey(type: TaskLinkType, meta?: TaskLinkMeta): LinkEntityKey {
  switch (type) {
    case "idea":
      return "idea";
    case "brief":
      return "brief";
    case "post":
      return meta?.postType === "story" ? "story" : "post";
    case "calendar_event":
      return "calendarEvent";
  }
}

export interface LinkEntity {
  icon: ComponentType<IconProps>;
  label: string;
  plural: string;
}

/** The ENTITY_CONFIG entry that names and draws a linked item. */
export function linkEntity(type: TaskLinkType, meta?: TaskLinkMeta): LinkEntity {
  return ENTITY_CONFIG[linkEntityKey(type, meta)];
}

export interface MarkedLinkOption extends TaskLinkOption {
  linked: boolean;
}

/** Picker rows, with the items the task already links directly marked (the
 * picker disables them). A carried item is no direct link: it stays
 * pickable. */
export function markLinkedOptions(options: TaskLinkOption[], links: TaskLinkRef[]): MarkedLinkOption[] {
  return options.map((option) => ({
    ...option,
    linked: links.some((link) => link.type === option.type && link.targetId === option.id),
  }));
}
